/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @material-ui core components
import React, { useContext } from 'react'

import Autocomplete from '@mui/material/Autocomplete'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'

import MDAvatar from 'components/MDAvatar'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import { PREFERRED_CONTACT_TYPE_OPTIONS } from 'layouts/feedback/constants'
import FormField from 'modules/FormField'
import { toTitleCase } from 'utils/string'

import { FormContext } from '../../constants'
import { CustomerFeedback } from '../../types'

function Customer(): JSX.Element {
	const formContext = useContext(FormContext)
	const { localForm, setFormEdited, setLocalForm, showError } = formContext

	const handleFormUpdate = (value: string, field: keyof CustomerFeedback['form']['customer']) => {
		setLocalForm(prev => ({
			...prev,
			form: {
				...prev.form,
				customer: {
					...prev.form.customer,
					[field]: value,
				},
			},
		}))
		setFormEdited(true)
	}

	return (
		<Card id="provider" sx={{ overflow: 'visible' }}>
			<MDBox display={'flex'} flexDirection={'row'} gap={2} p={3}>
				<MDAvatar bgColor="primary" size="xs">
					2
				</MDAvatar>
				<MDTypography variant="h5" fontWeight="medium">
					Customer Details
				</MDTypography>
			</MDBox>
			<MDBox component="form" pb={3} px={3}>
				<Grid container spacing={3}>
					<Grid item xs={12} sm={6}>
						<FormField
							value={localForm.form.customer.name}
							label="Name"
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'name')
							}}
							error={showError && !localForm.form.customer.name.trim()}
							helperText={
								showError && !localForm.form.customer.name.trim() ? 'Name is required' : ''
							}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={3}>
						<Autocomplete
							value={toTitleCase(localForm.form.customer.preferredContactType)}
							options={PREFERRED_CONTACT_TYPE_OPTIONS.map(c => toTitleCase(c))}
							renderInput={params => (
								<FormField
									{...params}
									label="Preferred Contact"
									InputLabelProps={{ shrink: true }}
									error={showError && !localForm.form.customer.preferredContactType}
									helperText={
										showError && !localForm.form.customer.preferredContactType
											? 'Preferred contact type is required'
											: ''
									}
								/>
							)}
							onChange={(_, newValue) => {
								handleFormUpdate(newValue.toUpperCase(), 'preferredContactType')
							}}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<FormField
							label="Preferred Date/Time"
							inputProps={{ type: 'datetime-local' }}
							value={localForm.form.customer.preferredDatetime}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'preferredDatetime')
							}}
						/>
					</Grid>

					<Grid item xs={12} sm={6}>
						<FormField
							label="Email"
							placeholder="example@email.com"
							inputProps={{ type: 'email' }}
							autoComplete="off"
							value={localForm.form?.customer.email}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'email')
							}}
							error={
								showError &&
								(!localForm.form.customer.email.trim() ||
									!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(localForm.form.customer.email))
							}
							helperText={
								showError && !localForm.form.customer.email.trim()
									? 'Email is required'
									: showError &&
										  !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(
												localForm.form.customer.email
										  )
										? 'Enter a valid email address'
										: ''
							}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<FormField
							label="Phone Number"
							placeholder="+447123456789"
							value={localForm.form.customer.phoneNumber}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'phoneNumber')
							}}
							error={showError && !localForm.form.customer.phoneNumber.trim()}
							helperText={
								showError && !localForm.form.customer.phoneNumber.trim()
									? 'Phone number is required'
									: ''
							}
						/>
					</Grid>
				</Grid>
			</MDBox>
		</Card>
	)
}

export default Customer
